import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import GenerationMixChart from "../../features/grid/components/GenerationMixChart";
import { useGridDetail, useGridHistory, useGenerationMix } from "../../features/grid/hooks/useGridData";
import {
  type GridMetricKey,
  METRIC_LABELS,
  METRIC_UNITS,
  ENDPOINT_METRICS,
  MIDPOINT_METRICS,
} from "../../api/grid";
import { fetchBAImpacts } from "../../api/pipeline";

const ENDPOINT_DESCRIPTIONS: Record<string, string> = {
  carbon_intensity_kg_per_mwh: "CO\u2082 displaced per MWh of clean energy",
  human_health_dalys: "DALYs displaced per MWh of clean energy",
  ecosystem_quality_species_yr: "Biodiversity preserved per MWh generated",
  resource_scarcity_usd2013: "Economic resource value saved per MWh",
};

function fmtVal(v: number | null | undefined): string {
  if (v == null) return "N/A";
  if (Math.abs(v) < 0.001 && v !== 0) return v.toExponential(3);
  return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

const GridRegionDetail: React.FC = () => {
  const { baCode } = useParams<{ baCode: string }>();
  const { data: detail, isLoading } = useGridDetail(baCode ?? null);
  const { data: history } = useGridHistory(baCode ?? null);
  const { data: genMix } = useGenerationMix(baCode ?? null);
  const { data: baImpacts } = useQuery({
    queryKey: ["pipelineBAImpacts"],
    queryFn: fetchBAImpacts,
    staleTime: 5 * 60 * 1000,
  });

  const pipelineForBA = useMemo(() => {
    if (!baCode || !baImpacts?.bas) return null;
    return baImpacts.bas.find((b) => b.ba_code === baCode) ?? null;
  }, [baCode, baImpacts]);

  if (!baCode) return null;

  const chartData =
    history?.impacts.map((imp) => ({
      time: new Date(imp.timestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false }),
      co2: imp.carbon_intensity_kg_per_mwh,
      dalys: imp.human_health_dalys,
    })) ?? [];

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/grid"
          className="text-text-muted hover:text-text-primary transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-h2 font-bold text-text-primary">{baCode}</h1>
          <p className="text-body-sm text-text-muted">Grid impact detail</p>
        </div>
      </div>

      {/* Impact context banner */}
      <div className="bg-quantum-500/10 border border-quantum-500/20 rounded-xl px-5 py-4 mb-8">
        <p className="text-body-sm text-text-secondary">
          These are the hourly environmental impacts of grid electricity in <strong className="text-text-primary">{baCode}</strong>.
          Every MWh your renewable project generates displaces these impacts.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 py-12 justify-center">
          <div className="w-5 h-5 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-body-sm text-text-secondary">Loading...</span>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Endpoint metrics cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ENDPOINT_METRICS.map(({ key, label }) => (
              <div key={key} className="bg-surface-800 rounded-xl p-4 border border-border">
                <p className="text-caption text-text-muted mb-1">{label}</p>
                <p className="text-xl font-bold text-text-primary">
                  {fmtVal(detail?.[key] as number)}
                </p>
                <p className="text-caption text-text-muted">{METRIC_UNITS[key]}</p>
                <p className="text-caption text-quantum-400/80 mt-1">
                  {ENDPOINT_DESCRIPTIONS[key]}
                </p>
              </div>
            ))}
          </div>

          {/* 24h chart */}
          {chartData.length > 0 && (
            <div className="bg-surface-800 rounded-xl p-5 border border-border">
              <h3 className="text-body font-semibold text-text-primary mb-4">
                Carbon Intensity — Last 24 Hours
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="time" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      color: "#e2e8f0",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="co2"
                    stroke="#00b4d8"
                    strokeWidth={2}
                    dot={false}
                    name="kg CO&#x2082;/MWh"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Generation Mix */}
          {genMix && genMix.fuels.length > 0 && (
            <div className="bg-surface-800 rounded-xl p-5 border border-border">
              <h3 className="text-body font-semibold text-text-primary mb-4">
                Generation Mix
              </h3>
              <GenerationMixChart fuels={genMix.fuels} />
            </div>
          )}

          {/* ReCiPe midpoint breakdown table */}
          <div className="bg-surface-800 rounded-xl p-5 border border-border">
            <h3 className="text-body font-semibold text-text-primary mb-1">
              Environmental Impact Intensity — per MWh
            </h3>
            <p className="text-caption text-text-muted mb-4">
              Full ReCiPe 2016 midpoint categories normalized by grid generation
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-text-muted font-medium">Category</th>
                    <th className="text-right py-2 text-text-muted font-medium">Value/MWh</th>
                    <th className="text-left py-2 pl-3 text-text-muted font-medium">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {MIDPOINT_METRICS.map(({ key, label, unit }) => (
                    <tr key={key} className="border-b border-border/50 hover:bg-surface-700/50">
                      <td className="py-2 text-text-secondary">{label}</td>
                      <td className="py-2 text-right font-mono text-text-primary">
                        {fmtVal((detail as any)?.[key])}
                      </td>
                      <td className="py-2 pl-3 text-text-muted">{unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Planned Pipeline */}
          {pipelineForBA && pipelineForBA.generator_count > 0 && (
            <div className="bg-surface-800 rounded-xl p-5 border-2 border-green-500/30">
              <h3 className="text-body font-semibold text-green-400 mb-4">
                Planned Pipeline in {baCode}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-caption text-text-muted">Planned Generators</p>
                  <p className="text-xl font-bold text-text-primary">
                    {pipelineForBA.generator_count}
                  </p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">Total Capacity</p>
                  <p className="text-xl font-bold text-text-primary">
                    {pipelineForBA.total_capacity_mw.toLocaleString(undefined, { maximumFractionDigits: 0 })} MW
                  </p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">Annual Generation</p>
                  <p className="text-xl font-bold text-text-primary">
                    {(pipelineForBA.annual_generation_mwh / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} GWh
                  </p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">DALYs Avoided</p>
                  <p className="text-xl font-bold text-green-400">
                    {pipelineForBA.dalys_avoided.toExponential(2)}
                  </p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">Ecosystem Benefit</p>
                  <p className="text-xl font-bold text-green-400">
                    {pipelineForBA.pdfs_avoided.toExponential(2)} species.yr
                  </p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">Economic Value</p>
                  <p className="text-xl font-bold text-green-400">
                    ${pipelineForBA.usd_avoided.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-green-500/20">
                <Link
                  to={`/generators?ba_code=${baCode}&status=P,L,T,U,V,TS`}
                  className="text-body-sm text-green-400 hover:text-green-300 transition-colors"
                >
                  Browse planned generators &rarr;
                </Link>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-surface-800/50 border border-border rounded-xl p-6 text-center">
            <p className="text-body text-text-secondary mb-4">
              See how your project compares — get a full lifecycle analysis
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to={`/generators?ba_code=${baCode}`}
                className="btn-primary px-6 py-2"
              >
                See generators in this region
              </Link>
              <Link
                to="/login"
                className="text-body-sm text-quantum-400 hover:text-quantum-300 transition-colors"
              >
                Sign in for full analysis
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GridRegionDetail;
